/**
 * Goods Receipts Table Component
 *
 * First table migrated from DevExpress `BStockDataGrid` to `@bill/ui/DataGrid`.
 * Serves as the pilot for server-side sort validation — the `sortState` /
 * `onSortChange` controlled mode is exercised here and will be replicated
 * across all other BStock listing tables.
 *
 * @see US-9.4 - Page Liste Receptions
 * @see sprint-datagrid-migration Phase 1.4
 */

import { useMemo, useCallback } from 'react';
import { Check, X, Trash2, MoreVertical, FileText } from 'lucide-react';
import {
  DataGrid,
  Badge,
  Dropdown,
  DropdownItem,
  textColumn,
  dateTimeColumn,
  moneyColumn,
  integerColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency } from '@/hooks';
import type {
  GoodsReceiptListItem,
  QueryGoodsReceiptsDto,
} from '@bstock/shared';
import {
  GoodsReceiptStatus,
  GOODS_RECEIPT_STATUS_LABELS,
  GOODS_RECEIPT_STATUS_COLORS,
} from '@bstock/shared';

type SortField = QueryGoodsReceiptsDto['sortBy'];
type SortOrder = QueryGoodsReceiptsDto['sortOrder'];

interface GoodsReceiptsTableProps {
  receipts: GoodsReceiptListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onRowClick: (receipt: GoodsReceiptListItem) => void;
  onValidate?: (receipt: GoodsReceiptListItem) => void;
  onCancel?: (receipt: GoodsReceiptListItem) => void;
  onDelete?: (receipt: GoodsReceiptListItem) => void;
  onGeneratePdf?: (receipt: GoodsReceiptListItem) => void;
  onSort: (field: SortField, order: SortOrder) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onFetchAllRows?: () => Promise<GoodsReceiptListItem[]>;
  showFinancials?: boolean;
}

function getStatusVariant(status: GoodsReceiptStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    red: 'danger',
  };
  return colorMap[GOODS_RECEIPT_STATUS_COLORS[status]] ?? 'neutral';
}

/**
 * Map between DataGrid column ids and the backend's accepted `sortBy`
 * values. Only columns listed here are sortable — everything else gets
 * `sortable: false` automatically because `sortState` is controlled.
 */
const SORT_FIELD_MAP: Record<string, SortField> = {
  receiptNumber: 'receiptNumber',
  receiptDate: 'receiptDate',
  totalExclTax: 'totalExclTax',
};

export function GoodsReceiptsTable({
  receipts,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onRowClick,
  onValidate,
  onCancel,
  onDelete,
  onGeneratePdf,
  onSort,
  onPageChange,
  onLimitChange,
  onFetchAllRows,
  showFinancials = true,
}: GoodsReceiptsTableProps) {
  const currency = useCurrency();
  const hasAnyAction = onValidate || onCancel || onDelete || onGeneratePdf;

  // ---- Columns -----------------------------------------------------------
  const columns = useMemo<DataGridColumn<GoodsReceiptListItem>[]>(() => {
    const cols: DataGridColumn<GoodsReceiptListItem>[] = [
      textColumn<GoodsReceiptListItem>({
        id: 'receiptNumber',
        header: 'Numéro',
        accessor: 'receiptNumber',
        cellClassName: 'font-mono',
      }),
      dateTimeColumn<GoodsReceiptListItem>({
        id: 'receiptDate',
        header: 'Date',
        accessor: (r) => new Date(r.receiptDate),
      }),
      textColumn<GoodsReceiptListItem>({
        id: 'supplier',
        header: 'Fournisseur',
        sortable: false,
        accessor: (r) => r.supplier.name,
        ellipsis: true,
        maxWidth: '220px',
      }),
      textColumn<GoodsReceiptListItem>({
        id: 'purchaseOrder',
        header: 'Commande',
        sortable: false,
        accessor: (r) => r.purchaseOrder?.orderNumber ?? null,
        cell: (r) => (
          <span className="text-sm text-dark-500">
            {r.purchaseOrder?.orderNumber ?? '—'}
          </span>
        ),
      }),
      integerColumn<GoodsReceiptListItem>({
        id: 'itemsCount',
        header: 'Lignes',
        accessor: 'itemsCount',
        sortable: false,
        align: 'center',
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<GoodsReceiptListItem>({
          id: 'totalExclTax',
          header: 'Total HT',
          // API returns centimes → convert to main currency unit
          accessor: (r) => r.totalExclTax / 100,
        })
      );
    }

    cols.push(
      textColumn<GoodsReceiptListItem>({
        id: 'status',
        header: 'Statut',
        align: 'center',
        sortable: false,
        exportValue: (r) => GOODS_RECEIPT_STATUS_LABELS[r.status],
        cell: (r) => (
          <div className="flex items-center justify-center gap-1.5">
            <Badge variant={getStatusVariant(r.status)}>
              {GOODS_RECEIPT_STATUS_LABELS[r.status]}
            </Badge>
            {!r.isDefinitive && <Badge variant="warning">Provisoire</Badge>}
          </div>
        ),
      })
    );

    if (hasAnyAction) {
      cols.push(
        textColumn<GoodsReceiptListItem>({
          id: 'actions',
          header: '',
          sortable: false,
          exportSkip: true,
          width: '50px',
          align: 'center',
          cell: (r) => {
            const isDraft = r.status === GoodsReceiptStatus.DRAFT;
            const isValidated = r.status === GoodsReceiptStatus.VALIDATED;
            const showValidate = onValidate && isDraft;
            const showDelete = onDelete && isDraft;
            const showCancel = onCancel && isValidated;
            const showGeneratePdf = onGeneratePdf && isValidated;
            if (!showValidate && !showDelete && !showCancel && !showGeneratePdf) return null;

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  trigger={
                    <button className="p-1 rounded hover:bg-neutral-100">
                      <MoreVertical className="w-4 h-4 text-neutral-500" />
                    </button>
                  }
                >
                  {showValidate && (
                    <DropdownItem icon={<Check className="w-4 h-4" />} onClick={() => onValidate(r)}>
                      Valider
                    </DropdownItem>
                  )}
                  {showDelete && (
                    <DropdownItem
                      icon={<Trash2 className="w-4 h-4" />}
                      onClick={() => onDelete(r)}
                      className="text-danger-600 hover:bg-danger-50"
                    >
                      Supprimer
                    </DropdownItem>
                  )}
                  {showGeneratePdf && (
                    <DropdownItem icon={<FileText className="w-4 h-4" />} onClick={() => onGeneratePdf(r)}>
                      Générer PDF
                    </DropdownItem>
                  )}
                  {showCancel && (
                    <DropdownItem
                      icon={<X className="w-4 h-4" />}
                      onClick={() => onCancel(r)}
                      className="text-danger-600 hover:bg-danger-50"
                    >
                      Annuler
                    </DropdownItem>
                  )}
                </Dropdown>
              </div>
            );
          },
        })
      );
    }

    return cols;
  }, [currency, onValidate, onCancel, onDelete, onGeneratePdf, showFinancials, hasAnyAction]);

  // ---- Sort (server-side, controlled) ------------------------------------
  //
  // The DataGrid's `sortState` is derived from the parent's URL params
  // (`sortBy` / `sortOrder`). When the user clicks a column header, the
  // grid calls `onSortChange` and we translate the column id back to
  // the backend's `sortBy` enum value.
  //
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, backendField]) => backendField === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('receiptDate' as SortField, 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) {
        onSort(backendField, next.direction);
      }
    },
    [onSort]
  );

  return (
    <DataGrid<GoodsReceiptListItem>
      data={receipts}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={onRowClick}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'receptions' },
        sheetName: 'Réceptions',
        position: 'header',
        onFetchAllRows,
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune réception trouvée
        </div>
      }
    />
  );
}
